/* WeatherLayer — GPU-friendly particle overlay driven by the battle's
 * arena + weather fields. Uses CSS transforms and opacity only — no
 * canvas, no heavy libs, ~24 elements max. Respects reduced motion.
 *
 * PERF: memoised end-to-end. The particle bag is generated ONCE per
 * (kind, motionOk) combo and cached, so re-renders of the parent
 * arena (HP changes, floaters, awaiting states) do not thrash the DOM.
 */
import { memo, useMemo } from 'react';

const CELL_COUNT = 24;

function seededDots(kind, motionOk) {
  const dots = [];
  for (let i = 0; i < CELL_COUNT; i++) {
    const left = Math.random() * 100;
    const delay = Math.random() * 2;
    const duration = 1 + Math.random() * 2.5;
    dots.push(
      <span
        key={`${kind}-${i}`}
        className={
          `absolute pointer-events-none ${motionOk ? '' : 'hidden'}`
        }
        style={
          kind === 'rain' ? {
            left: `${left}%`,
            top: '-10%',
            width: '1px',
            height: '12px',
            background: 'linear-gradient(to bottom, rgba(147,197,253,0), rgba(147,197,253,0.6))',
            animation: `wq-rain ${duration}s linear ${delay}s infinite`,
          } : kind === 'snow' ? {
            left: `${left}%`,
            top: '-5%',
            width: '3px',
            height: '3px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.8)',
            filter: 'blur(0.5px)',
            animation: `wq-snow ${3 + duration}s linear ${delay}s infinite`,
          } : kind === 'fog' ? {
            left: `${left}%`,
            top: `${Math.random() * 100}%`,
            width: '260px',
            height: '80px',
            borderRadius: '50%',
            background: 'radial-gradient(closest-side, rgba(255,255,255,0.14), rgba(255,255,255,0))',
            animation: `wq-fog ${8 + duration * 2}s linear ${delay}s infinite`,
          } : kind === 'wind' ? {
            left: `${left}%`,
            top: `${Math.random() * 100}%`,
            width: '18px',
            height: '1px',
            background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,0.5), rgba(255,255,255,0))',
            animation: `wq-wind ${duration * 1.2}s linear ${delay}s infinite`,
          } : kind === 'sun' ? {
            left: `${left}%`,
            top: `${Math.random() * 100}%`,
            width: '2px',
            height: '2px',
            borderRadius: '50%',
            background: 'rgba(253, 186, 116, 0.7)',
            filter: 'blur(1px)',
            animation: `wq-pulse ${3 + duration}s ease-in-out ${delay}s infinite`,
          } : {
            display: 'none',
          }
        }
      />
    );
  }
  return dots;
}

const ARENA_BG = {
  forest:   'radial-gradient(ellipse at 20% 20%, #204a3a55 0%, transparent 60%), linear-gradient(180deg, #0a1a12 0%, #04100a 100%)',
  wetland:  'radial-gradient(ellipse at 50% 30%, #1a3a4f55 0%, transparent 60%), linear-gradient(180deg, #0a1219 0%, #04080d 100%)',
  river:    'radial-gradient(ellipse at 50% 60%, #1e546a55 0%, transparent 60%), linear-gradient(180deg, #0a1720 0%, #030a10 100%)',
  grassland:'radial-gradient(ellipse at 50% 40%, #4a663a55 0%, transparent 60%), linear-gradient(180deg, #14200c 0%, #060a04 100%)',
  mountain: 'radial-gradient(ellipse at 30% 30%, #4a4a5a55 0%, transparent 60%), linear-gradient(180deg, #131820 0%, #05070a 100%)',
  coast:    'radial-gradient(ellipse at 50% 55%, #d4a52755 0%, transparent 55%), linear-gradient(180deg, #12241b 0%, #050c08 100%)',
  urban:    'radial-gradient(ellipse at 40% 40%, #2a2e3a55 0%, transparent 60%), linear-gradient(180deg, #14161c 0%, #05070a 100%)',
  night:    'radial-gradient(ellipse at 50% 30%, #1a1e5a55 0%, transparent 60%), linear-gradient(180deg, #050612 0%, #010104 100%)',
};

/* Real ecology photographs served from /public/assets/battlefields/.
 * Each arena tag from the backend maps to the most photographic match
 * we have on hand. `ARENA_PHOTO` is the primary hero — if the file is
 * missing (404) the gradient fallback in ARENA_BG paints underneath so
 * the cards never sit on a black abyss. */
const ARENA_PHOTO = {
  forest:   '/assets/battlefields/conifer_falls_1.webp',
  wetlands: '/assets/battlefields/cypress_swamp_1.webp',
  wetland:  '/assets/battlefields/cypress_swamp_1.webp',
  swamp:    '/assets/battlefields/haunted_swamp_1.webp',
  river:    '/assets/battlefields/rocky_riverbank_1.webp',
  lake:     '/assets/battlefields/alpine_lake_1.webp',
  grassland:'/assets/battlefields/valley_meadow_1.webp',
  meadow:   '/assets/battlefields/meadow_1.webp',
  mountain: '/assets/battlefields/mountain_peak_1.webp',
  cave:     '/assets/battlefields/mountain_temple_1.webp',
  coast:    '/assets/battlefields/coastal_cliff_1.webp',
  desert:   '/assets/battlefields/desert_1.webp',
  tundra:   '/assets/battlefields/tundra_1.webp',
  night:    '/assets/battlefields/night_forest_1.webp',
  urban:    '/assets/battlefields/mountain_temple_1.webp',
};

export default memo(function WeatherLayer({ arena, weather, motionOk = true }) {
  const key = (arena || '').toLowerCase();
  const bg = ARENA_BG[key] || ARENA_BG.forest;
  const photo = ARENA_PHOTO[key] || ARENA_PHOTO.forest;
  const kind = (weather || '').toLowerCase();
  const showLightning = kind === 'storm';
  const particles = useMemo(
    () => (['rain', 'snow', 'fog', 'wind', 'sun'].includes(kind)
      ? seededDots(kind, motionOk)
      : null),
    [kind, motionOk],
  );
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="weather-layer">
      {/* Gradient fallback so the arena is never a black abyss if the
          photo 404s or is still downloading. */}
      <div className="absolute inset-0" style={{ background: bg }} />
      {/* Real ecology background — a photograph of the biome the cards
          are fighting in. Slight blur + darkened overlay keep the two
          card portraits as the visual anchor. */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${photo})`,
          filter: 'brightness(0.72) saturate(1.08)',
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-black/45" />
      {/* Sun rays overlay */}
      {kind === 'sun' && (
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse at 30% 0%, rgba(253,186,116,0.4) 0%, transparent 45%)',
          }}
        />
      )}
      {/* Fog haze — plain bg tint on mobile; backdrop-blur is a mobile GPU tax. */}
      {kind === 'fog' && (
        <div className="absolute inset-0 bg-slate-300/15" />
      )}
      {/* Particle layer */}
      {particles}
      {/* Storm lightning — occasional flash. */}
      {showLightning && motionOk && (
        <div
          className="absolute inset-0 bg-white/60"
          style={{ animation: 'wq-lightning 7s ease-in-out infinite' }}
        />
      )}
      {/* Vignette so photos remain the visual anchor */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.65) 100%)',
      }} />
    </div>
  );
});
